import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { qaApi } from "../../services/api/qaApi";
import { useUserContext } from "../../context/UserContext";
import FeedSidebar from "./FeedSidebar";
import FeedPost from "./FeedPost";
import PostSkeleton from "./PostSkeleton";
import { FaAward, FaFilter } from "react-icons/fa";
import "./BountyQuestionsPage.css";

const BountyQuestionsPage = () => {
  const { user } = useUserContext();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const LIMIT = 20;

  const loadQuestions = useCallback(
    async (pageNum = 0, append = false) => {
      try {
        if (pageNum === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const response = await qaApi.getBountyQuestions({
          limit: LIMIT,
          offset: pageNum * LIMIT,
        });

        if (response.data.success) {
          const newQuestions = response.data.questions || [];

          if (append) {
            setQuestions((prev) => [...prev, ...newQuestions]);
          } else {
            setQuestions(newQuestions);
          }

          setHasMore(newQuestions.length === LIMIT);
        }
      } catch (error) {
        // Silent fail
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    setPage(0);
    loadQuestions(0, false);
  }, [loadQuestions]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadQuestions(nextPage, true);
  };

  const handlePostUpdate = (postId, updates) => {
    setQuestions((prev) =>
      prev.map((q) => (q._id === postId ? { ...q, ...updates } : q))
    );
  };

  const handlePostDelete = (postId) => {
    setQuestions((prev) => prev.filter((q) => q._id !== postId));
  };

  if (!user) {
    return (
      <div className="bounty-questions-page">
        <div className="bqp-container">
          <div className="bqp-auth-required">
            <h2>Please log in to view bounty questions</h2>
            <Link to="/" className="bqp-login-btn">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bounty-questions-page">
      <div className="bqp-layout">
      <div className="bqp-main">
        <div className="bqp-header">
          <div className="bqp-header-info">
            <h1>
              <FaAward className="bqp-icon" /> Active Bounty Questions
            </h1>
            <p>Questions with active bounties — earn reputation by answering</p>
          </div>
        </div>

        <div className="bqp-content">
          {loading ? (
            <div className="bqp-loading">
              {[...Array(5)].map((_, i) => (
                <PostSkeleton key={i} />
              ))}
            </div>
          ) : questions.length === 0 ? (
            <div className="bqp-empty">
              <FaAward className="bqp-empty-icon" />
              <h3>No bounty questions</h3>
              <p>There are no questions with active bounties right now. Check back later!</p>
            </div>
          ) : (
            <>
              <div className="bqp-questions-list">
                {questions.map((question) => (
                  <FeedPost
                    key={question._id}
                    post={question}
                    onPostUpdate={handlePostUpdate}
                    onPostDelete={handlePostDelete}
                  />
                ))}
              </div>

              {hasMore && (
                <div className="bqp-load-more">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="bqp-load-more-btn"
                  >
                    {loadingMore ? "Loading..." : "Load More Bounty Questions"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <FeedSidebar />
      </div>
    </div>
  );
};

export default BountyQuestionsPage;
